import React, { useMemo, useState } from 'react';
import { Alert, Box, Button, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from 'recharts';
import { fmtMoney } from '../../../Documents/format';
import { BUCKET_COLORS, CHART } from '../../constants';
import { fmtCompact, fmtDay, fmtInt, fmtPct, share } from '../../format';
import { journalApi } from '../../journalApi';
import { useLoad } from '../../hooks/useLoad';
import { BucketKey, BucketRow, JournalFilters, OptionField, OptionPick, StructureResponse, StructureRow, TopProduct } from '../../types';
import {
  AXIS_TICK, BORDER, ChartCard, ChartSkeleton, NoData, ShareBar, SubText, TABLE_SX, TipBox, TooltipInjected, clip, tooltipPoint
} from './chartKit';

interface Props {
  filters: JournalFilters;
  paramsKey: string;
  active: boolean;
  reloadToken: number;
  onPatch: (patch: Partial<JournalFilters>) => void;
  onPick: (field: OptionField, pick: OptionPick | null) => void;
  /** Открыть карточку товара на вкладке «Товар» */
  onProduct: (pick: OptionPick) => void;
}

// ===== Сроки годности: сейчас и на дату записи =====

const CompositionBar: React.FC<{ title: string; hint: string; rows: BucketRow[]; total: number }> = ({ title, hint, rows, total }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '150px 1fr' }, gap: { xs: 0.5, sm: 1.5 }, alignItems: 'center' }}>
    <Box>
      <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#0f172a' }}>{title}</Typography>
      <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8' }}>{hint}</Typography>
    </Box>
    <Box sx={{ display: 'flex', height: 26, borderRadius: 1.5, overflow: 'hidden', bgcolor: '#f1f5f9' }}>
      {rows.filter(r => r.sum > 0).map(r => {
        const pct = share(r.sum, total);
        return (
          <Tooltip
            key={r.key} arrow
            title={`${r.label}: ${fmtMoney(r.sum)} · ${fmtPct(pct)} · ${fmtInt(r.count)} зап.`}
          >
            <Box sx={{
              width: `${pct}%`, minWidth: 2, bgcolor: BUCKET_COLORS[r.key].color, color: '#fff', fontSize: '0.68rem', fontWeight: 600,
              display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', whiteSpace: 'nowrap',
              borderRight: '1px solid rgba(255,255,255,0.6)'
            }}>
              {pct >= 7 ? fmtPct(pct, 0) : ''}
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  </Box>
);

const ExpiryCard: React.FC<{ data: StructureResponse; bucket: JournalFilters['bucket']; onPatch: Props['onPatch'] }> = ({ data, bucket, onPatch }) => {
  const total = data.totals.sum;
  const atRecord = useMemo(() => {
    const map: Partial<Record<BucketKey, BucketRow>> = {};
    data.expiryAtRecord.forEach(r => { map[r.key] = r; });
    return map;
  }, [data.expiryAtRecord]);
  const urgentNow = data.expiryNow.filter(r => r.key === 'expired' || r.key === 'd30' || r.key === 'd90').reduce((s, r) => s + r.sum, 0);
  const urgentAtRecord = data.expiryAtRecord.filter(r => r.key === 'expired' || r.key === 'd30' || r.key === 'd90').reduce((s, r) => s + r.sum, 0);

  const pick = (key: BucketKey) => {
    if (key === 'unknown') return;
    onPatch(bucket === key ? { bucket: '' } : { bucket: key, expiryFrom: '', expiryTo: '' });
  };

  return (
    <ChartCard
      wide title="Остаток срока годности"
      subtitle={`Сейчас - на сегодня (${fmtDay(data.today)}); на дату записи - сколько оставалось, когда аптека вносила товар. До 90 дней сейчас: ${fmtPct(share(urgentNow, total))} суммы, при вводе было ${fmtPct(share(urgentAtRecord, total))}.`}
      actions={bucket ? <Chip size="small" label="Снять фильтр по сроку" onDelete={() => onPatch({ bucket: '' })} /> : undefined}
    >
      {!data.totals.count ? <NoData height={120} /> : (
        <>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1.5 }}>
            <CompositionBar title="Сейчас" hint="по сегодняшней дате" rows={data.expiryNow} total={total} />
            <CompositionBar title="На дату записи" hint="при вводе в журнал" rows={data.expiryAtRecord} total={total} />
          </Box>
          <TableContainer sx={{ border: `1px solid ${BORDER}`, borderRadius: 1.5 }}>
            <Table size="small" sx={TABLE_SX}>
              <TableHead>
                <TableRow>
                  <TableCell>Остаток срока</TableCell>
                  <TableCell align="right">Сумма сейчас</TableCell>
                  <TableCell sx={{ minWidth: 140 }}>Доля</TableCell>
                  <TableCell align="right">Записей</TableCell>
                  <TableCell align="right">Аптек</TableCell>
                  <TableCell align="right">Товаров</TableCell>
                  <TableCell align="right">Уп.</TableCell>
                  <TableCell align="right">На дату записи</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.expiryNow.map(r => {
                  const rec = atRecord[r.key];
                  const selected = bucket === r.key;
                  const clickable = r.key !== 'unknown';
                  return (
                    <TableRow
                      key={r.key} hover={clickable} selected={selected} onClick={() => pick(r.key)}
                      sx={{ cursor: clickable ? 'pointer' : 'default', '&.Mui-selected td': { bgcolor: BUCKET_COLORS[r.key].bg } }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: BUCKET_COLORS[r.key].color, flexShrink: 0 }} />
                          <Typography sx={{ fontSize: '0.78rem', fontWeight: selected ? 700 : 500 }}>{r.label}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{fmtMoney(r.sum)}</TableCell>
                      <TableCell><ShareBar value={share(r.sum, total)} color={BUCKET_COLORS[r.key].color} label={fmtPct(share(r.sum, total))} /></TableCell>
                      <TableCell align="right">{fmtInt(r.count)}</TableCell>
                      <TableCell align="right">{fmtInt(r.pharmacies)}</TableCell>
                      <TableCell align="right">{fmtInt(r.products)}</TableCell>
                      <TableCell align="right">{fmtInt(r.qty)}</TableCell>
                      <TableCell align="right">
                        {rec ? fmtMoney(rec.sum) : '-'}
                        {rec && <SubText>{fmtPct(share(rec.sum, total))} · {fmtInt(rec.count)} зап.</SubText>}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8', mt: 0.75 }}>
            Клик по строке - отфильтровать журнал и все графики по остатку срока (по сегодняшней дате).
          </Typography>
        </>
      )}
    </ChartCard>
  );
};

// ===== Разрезы: категория, менеджер, регион, ценовая группа, производитель =====

type BreakdownPoint = StructureRow & { rest: number };

const BREAKDOWN_LIMIT = 12;

const BreakdownTip: React.FC<TooltipInjected<BreakdownPoint> & { total?: number; clickable?: boolean }> = props => {
  const p = tooltipPoint(props);
  if (!p) return null;
  const canPick = props.clickable && p.value !== null && !p.others;
  return (
    <TipBox
      width={290} title={p.label}
      subtitle={p.others ? 'Остальные значения вместе' : undefined}
      rows={[
        { label: 'Сумма', value: fmtMoney(p.sum), bold: true, color: CHART.sum, hint: props.total ? `${fmtPct(share(p.sum, props.total))} от суммы по фильтрам` : undefined },
        { label: 'Записей', value: fmtInt(p.count) },
        { label: 'Количество, уп.', value: fmtInt(p.qty) },
        { label: 'Аптек / товаров', value: p.pharmacies === null ? 'не суммируется' : `${fmtInt(p.pharmacies)} / ${fmtInt(p.products)}` },
        { label: 'Просрочено', value: fmtMoney(p.expiredSum), color: CHART.expired, divider: true, hint: `${fmtPct(share(p.expiredSum, p.sum))} суммы` },
        { label: 'До 30 дней', value: fmtMoney(p.d30Sum), color: CHART.d30, hint: `${fmtPct(share(p.d30Sum, p.sum))} суммы` },
        { label: 'Остальное', value: fmtMoney(p.rest), color: '#93c5fd' }
      ]}
      footer={canPick ? 'Клик - отфильтровать журнал по этому значению' : p.value === null && !p.others ? 'Пустое значение: фильтр по нему недоступен' : undefined}
    />
  );
};

interface BreakdownProps {
  title: string;
  subtitle?: string;
  field: OptionField;
  rows: StructureRow[];
  total: number;
  pick: OptionPick | null;
  onPick: Props['onPick'];
  /** Строки уже ограничены сервером (топ + «Прочие») */
  limited?: boolean;
}

const BreakdownCard: React.FC<BreakdownProps> = ({ title, subtitle, field, rows, total, pick, onPick, limited }) => {
  const [expanded, setExpanded] = useState(false);
  const points = useMemo<BreakdownPoint[]>(
    () => rows.map(r => ({ ...r, rest: Math.max(0, r.sum - r.expiredSum - r.d30Sum) })),
    [rows]
  );
  const visible = expanded || limited ? points : points.slice(0, BREAKDOWN_LIMIT);
  const hiddenCount = points.length - visible.length;
  const hiddenSum = points.slice(visible.length).reduce((s, r) => s + r.sum, 0);
  const choose = (item: { payload?: unknown }) => {
    const p = item.payload as BreakdownPoint | undefined;
    if (p && p.value !== null && !p.others && !pick) onPick(field, { value: p.value, label: p.label });
  };
  const top = points[0];

  return (
    <ChartCard
      title={title}
      subtitle={subtitle || (top ? `${fmtInt(points.length)} знач. · лидер: ${clip(top.label, 40)} (${fmtPct(share(top.sum, total))})` : undefined)}
      actions={pick ? <Chip size="small" label={`Фильтр: ${clip(pick.label, 28)}`} onDelete={() => onPick(field, null)} /> : undefined}
    >
      {!points.length ? <NoData height={160} /> : (
        <>
          <Box sx={{ height: visible.length * 28 + 34 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={visible} layout="vertical" margin={{ top: 0, right: 12, left: 0, bottom: 0 }} barCategoryGap={5}>
                <CartesianGrid stroke={CHART.grid} horizontal={false} />
                <XAxis type="number" tick={AXIS_TICK} tickFormatter={v => fmtCompact(Number(v))} axisLine={{ stroke: BORDER }} tickLine={false} />
                <YAxis
                  type="category" dataKey="label" width={170} tick={{ ...AXIS_TICK, fill: '#334155' }} axisLine={false} tickLine={false} interval={0}
                  tickFormatter={v => clip(String(v), 26)}
                />
                <ChartTooltip content={<BreakdownTip total={total} clickable={!pick} />} cursor={{ fill: 'rgba(148,163,184,0.12)' }} />
                <Bar dataKey="expiredSum" stackId="b" fill={CHART.expired} isAnimationActive={false} onClick={choose} style={{ cursor: pick ? 'default' : 'pointer' }} />
                <Bar dataKey="d30Sum" stackId="b" fill={CHART.d30} isAnimationActive={false} onClick={choose} style={{ cursor: pick ? 'default' : 'pointer' }} />
                <Bar dataKey="rest" stackId="b" fill="#93c5fd" isAnimationActive={false} radius={[0, 3, 3, 0]} onClick={choose} style={{ cursor: pick ? 'default' : 'pointer' }} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', gap: 1.5, fontSize: '0.7rem', color: '#64748b' }}>
              {[['Просрочено', CHART.expired], ['До 30 дней', CHART.d30], ['Остальное', '#93c5fd']].map(([label, color]) => (
                <Box key={label} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: color }} />{label}
                </Box>
              ))}
            </Box>
            {!limited && points.length > BREAKDOWN_LIMIT && (
              <Button size="small" onClick={() => setExpanded(v => !v)} sx={{ textTransform: 'none', fontSize: '0.74rem', py: 0 }}>
                {expanded ? 'Свернуть' : `Ещё ${hiddenCount} (${fmtMoney(hiddenSum)})`}
              </Button>
            )}
          </Box>
        </>
      )}
    </ChartCard>
  );
};

// ===== Топ товаров =====

const TopProducts: React.FC<{ rows: TopProduct[]; total: number; onProduct: Props['onProduct'] }> = ({ rows, total, onProduct }) => {
  const max = rows.length ? rows[0].sum : 0;
  const topSum = rows.reduce((s, r) => s + r.sum, 0);
  return (
    <ChartCard
      wide title={`Топ-${rows.length} товаров по сумме`}
      subtitle={rows.length ? `Вместе ${fmtMoney(topSum)} - ${fmtPct(share(topSum, total))} суммы по фильтрам. Клик по строке - карточка товара.` : undefined}
    >
      {!rows.length ? <NoData height={120} /> : (
        <TableContainer sx={{ border: `1px solid ${BORDER}`, borderRadius: 1.5, maxHeight: 520 }}>
          <Table size="small" stickyHeader sx={TABLE_SX}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 28 }}>№</TableCell>
                <TableCell>Товар</TableCell>
                <TableCell>Категория / менеджер</TableCell>
                <TableCell align="right">Сумма</TableCell>
                <TableCell sx={{ minWidth: 120 }}>Доля</TableCell>
                <TableCell align="right">Записей</TableCell>
                <TableCell align="right">Аптек</TableCell>
                <TableCell align="right">Серий</TableCell>
                <TableCell align="right">Просрочено</TableCell>
                <TableCell align="right">До 30 дней</TableCell>
                <TableCell>Ближайший срок</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r, i) => (
                <TableRow key={r.code} hover onClick={() => onProduct({ value: r.code, label: r.name })} sx={{ cursor: 'pointer' }}>
                  <TableCell sx={{ color: '#94a3b8 !important' }}>{i + 1}</TableCell>
                  <TableCell sx={{ maxWidth: 320 }}>
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }} noWrap title={r.name}>{r.name}</Typography>
                    <SubText>{r.code}{r.producer ? ` · ${r.producer}` : ''}</SubText>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 200 }}>
                    <Typography sx={{ fontSize: '0.76rem' }} noWrap>{r.category || '-'}</Typography>
                    <SubText>{r.manager || 'менеджер не назначен'}</SubText>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {fmtMoney(r.sum)}
                    <SubText>{fmtInt(r.qty)} уп.</SubText>
                  </TableCell>
                  <TableCell><ShareBar value={r.sum} max={max} label={fmtPct(share(r.sum, total))} /></TableCell>
                  <TableCell align="right">{fmtInt(r.count)}</TableCell>
                  <TableCell align="right">{fmtInt(r.pharmacies)}</TableCell>
                  <TableCell align="right">{fmtInt(r.serials)}</TableCell>
                  <TableCell align="right" sx={{ color: r.expiredSum ? `${CHART.expired} !important` : undefined }}>
                    {r.expiredSum ? fmtMoney(r.expiredSum) : '-'}
                    {r.expiredSum > 0 && <SubText>{fmtPct(share(r.expiredSum, r.sum))}</SubText>}
                  </TableCell>
                  <TableCell align="right" sx={{ color: r.d30Sum ? `${CHART.d30} !important` : undefined }}>
                    {r.d30Sum ? fmtMoney(r.d30Sum) : '-'}
                  </TableCell>
                  <TableCell>{fmtDay(r.nearestExpiry)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </ChartCard>
  );
};

// ===== Вид «Структура» =====

const StructureView: React.FC<Props> = ({ filters, paramsKey, active, reloadToken, onPatch, onPick, onProduct }) => {
  const state = useLoad(
    active ? (silent: boolean, signal: AbortSignal) => journalApi.structure(filters, silent, signal) : null,
    paramsKey, reloadToken, 'Не удалось построить структуру журнала'
  );
  const data = state.data;

  if (!data) {
    return (
      <ChartCard wide title="Структура журнала" loading={state.loading} error={state.error?.text} onRetry={state.retry}>
        {!state.error && <ChartSkeleton height={420} />}
      </ChartCard>
    );
  }

  const total = data.totals.sum;
  const card = (field: OptionField, title: string, rows: StructureRow[], extra?: Partial<BreakdownProps>) => (
    <BreakdownCard title={title} field={field} rows={rows} total={total} pick={filters.picks[field]} onPick={onPick} {...extra} />
  );
  const producersShown = data.byProducer.filter(r => !r.others).length;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {state.error && (
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={state.retry}>Повторить</Button>}>
          {state.error.text}. На экране - данные прежнего запроса.
        </Alert>
      )}
      <Box sx={{ opacity: state.loading ? 0.65 : 1, transition: 'opacity 0.15s', display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 1.5 }}>
        <ExpiryCard data={data} bucket={filters.bucket} onPatch={onPatch} />
        {card('category', 'Категории', data.byCategory)}
        {card('manager', 'Менеджеры', data.byManager)}
        {card('region', 'Регионы', data.byRegion)}
        {card('priceGroup', 'Ценовые группы', data.byPriceGroup, { limited: true })}
        {card('producer', 'Производители', data.byProducer, {
          limited: true,
          subtitle: data.producersTotal > producersShown
            ? `Топ-${producersShown} из ${fmtInt(data.producersTotal)} по сумме, остальные - одной строкой`
            : `${fmtInt(data.producersTotal)} производителей`
        })}
        <TopProducts rows={data.topProducts} total={total} onProduct={onProduct} />
      </Box>
    </Box>
  );
};

export default React.memo(StructureView);
